// Support page. Renders an FAQ accordion and a "Questions or feedback?"
// contact form that POSTs to the public TaskPogoContact Lambda Function
// URL (see amplify/backend/function/TaskPogoContact). On success the form
// clears and we show a thank-you; on error we keep the user's text in the
// inputs and fall back to surfacing the info@taskpogo.com address so they
// can email us directly.

import { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import Alert from 'react-bootstrap/Alert';
import outputs from '../amplify_outputs.json';

// The contact Lambda's public Function URL is published into
// amplify_outputs.json by the Gen 2 backend
// (`backend.addOutput({ custom: { contactFunctionUrl } })` in
// amplify/backend.ts) and baked into the bundle when the build copies that file
// into src/. Each branch (dev/prod) thus gets its own URL with no
// environment-variable wiring. Read at call time (rather than destructured at
// module load) so tests can swap the value between cases.
function getContactEndpoint() {
  return outputs?.custom?.contactFunctionUrl || '';
}

// FAQ content. Keeping it inline (rather than in a separate JSON file) is
// the simplest thing -- there are only a handful of entries and they're
// English-only.
const faqs = [
  {
    id: 'item',
    q: 'What is an item?',
    a: (
      <>
        An <strong>item</strong> is a container for everything you track about
        one thing — most often a boat, but equally an airplane, a vehicle, a
        home, or a piece of equipment. Each item holds its own trips, tasks,
        logbooks of completed work, counters, sub-equipment (items nested
        inside it), and any documents or images you attach.
      </>
    ),
  },
  {
    // Second on purpose. The product leads with boating now, and this is the
    // most-read help page -- so the answer to "does this still work for my
    // hangar and my house?" belongs near the top, not buried at the bottom.
    id: 'not-just-boats',
    q: 'Does TaskPogo only work for boats?',
    a: (
      <>
        No. TaskPogo leads with boating because that's where trip planning and
        marine weather matter most, but <strong>every feature works for
        anything you maintain</strong> — airplanes, houses, vacation homes,
        cars, equipment, projects. Tasks, counters, logbooks, sub-equipment,
        attachments and email digests are identical whatever the item is, and
        an airplane gets a Pilot's Logbook the same way a boat gets a
        Captain's Log. Nothing is hidden or disabled based on what kind of
        thing an item is.
      </>
    ),
  },
  {
    id: 'trip',
    q: 'What is a trip?',
    a: (
      <>
        A <strong>trip</strong> is a series of legs — where you're going, when,
        and how many nights you're staying. TaskPogo groups the legs into one
        trip with its own page, route map and calendar, so a two-week cruise
        reads as a trip rather than as fourteen separate rows. Draw the route
        by dropping waypoints on the map and the distance is measured for you;
        places you name are remembered so the next trip can start from them.
      </>
    ),
  },
  {
    id: 'weather',
    q: 'Where does the weather come from?',
    a: (
      <>
        Marine area forecasts and warnings come from the{' '}
        <strong>US National Weather Service</strong> and{' '}
        <strong>Environment and Climate Change Canada</strong>; observations
        from <strong>NDBC buoys</strong> and airport stations; tide and tidal
        current predictions from <strong>NOAA</strong> and the{' '}
        <strong>Canadian Hydrographic Service</strong>. It's shown on the
        trip's own map, timed to the day you arrive rather than to today.{' '}
        <strong>
          For planning only — not for navigation. Always consult official
          forecasts and notices to mariners before going out.
        </strong>
      </>
    ),
  },
  {
    id: 'trip-weather-email',
    q: 'Can I get a daily weather email for a trip?',
    a: (
      <>
        Yes. Schedule one from a trip's page and it arrives each morning at an
        hour and time zone you pick: the marine areas and warnings for the
        waters you're crossing, conditions now, and the tides and currents for
        each leg. It stops on its own when the trip is over, and every message
        carries a one-click unsubscribe that needs no account.
      </>
    ),
  },
  {
    id: 'captains-log',
    q: "What is the Captain's Log?",
    a: (
      <>
        A <strong>Captain's Log</strong> records passages you've actually made
        — the date, where you went from and to, and the hours. Entries file
        themselves under the trip they belong to, so one trip's entries share a
        page and a map, and hours and trip counts total up by year. An airplane
        gets a <strong>Pilot's Logbook</strong> instead, with the fields a pilot
        expects; anything can also keep a plain <strong>Maintenance Log</strong>.
        An item can hold any mix of all three.
      </>
    ),
  },
  {
    id: 'places',
    q: 'What are places?',
    a: (
      <>
        <strong>Places</strong> is a shared list of the harbours, anchorages and
        marinas that trips refer to — with their positions and what's there
        (fuel, customs, a dinghy dock, a sandy beach). When you name a waypoint
        on a trip map it binds to a place, so correcting a position or a
        spelling once fixes it everywhere that place is used rather than in one
        trip only.
      </>
    ),
  },
  {
    id: 'task',
    q: 'What is a task?',
    a: (
      <>
        A <strong>task</strong> is something that needs to be done on an item.
        Tasks can be due by date, by a counter value (such as miles, engine
        hours, or aircraft cycles), both, or neither (just something that needs to be done at some point). 
        TaskPogo tells you what's overdue, what's coming up, and what's not due yet.
      </>
    ),
  },
  {
    id: 'counter',
    q: 'What is a counter?',
    a: (
      <>
        A <strong>counter</strong> is a measurement that drives when tasks
        come due. Common examples are a car's odometer, an engine's hour
        meter, or the number of landings on an aircraft. You record the
        current value as it changes; tasks tied to that counter use it to
        figure out whether they're due.
      </>
    ),
  },
  {
    id: 'logbook',
    q: 'What is the logbook?',
    a: (
      <>
        A <strong>logbook</strong> is the history of what you've done on an
        item. There are three kinds — a Captain's Log for a boat's passages, a
        Pilot's Logbook for an airplane's flights, and a Maintenance Log for
        the work on anything — and an item can hold any mix of them. Each entry captures the date the work was done, the counter
        values at that time, and any notes you want to keep. Completing a
        task automatically creates a logbook entry.
      </>
    ),
  },
  {
    id: 'sub-equipment',
    q: 'What is sub-equipment?',
    a: (
      <>
        <strong>Sub-equipment</strong> is one item nested inside another — for
        example, an engine inside a boat, or an avionics stack inside an
        airplane. Each piece of sub-equipment has its own tasks, counters,
        and logbook, so you can track maintenance on it independently.
      </>
    ),
  },
  {
    id: 'attachments',
    q: 'What are attached files for?',
    a: (
      <>
        You can attach any files you want to keep with an item: photos,
        checklists, maintenance manuals, receipts, inspection reports. They
        stay with the item so you always have them when you need them.
      </>
    ),
  },
  {
    id: 'cost',
    q: 'How much does TaskPogo cost?',
    a: (
      <>
        TaskPogo is <strong>free</strong> while we're in beta. If that ever
        changes, you'll hear from us first.
      </>
    ),
  },
];

const Status = {
  Idle: 'idle',
  Submitting: 'submitting',
  Success: 'success',
  Error: 'error',
};

// Cheap email shape check that mirrors the one on the Lambda; we only use
// it to disable the submit button until the field looks plausible. The real
// validation happens server-side.
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Support = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // Honeypot: real users never see or fill this. Bots that crawl every
  // input will, and the server will silently drop those submissions.
  const [company, setCompany] = useState('');

  const [status, setStatus] = useState(Status.Idle);
  const [statusMessage, setStatusMessage] = useState('');

  const trimmedEmail = email.trim();
  const trimmedMessage = message.trim();
  const canSubmit =
    status !== Status.Submitting
    && emailPattern.test(trimmedEmail)
    && trimmedMessage.length > 0;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;

    const endpoint = getContactEndpoint();
    if (!endpoint) {
      setStatus(Status.Error);
      setStatusMessage(
        'The contact form is not configured for this environment. Please email info@taskpogo.com directly.'
      );
      return;
    }

    setStatus(Status.Submitting);
    setStatusMessage('');

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: trimmedEmail,
          message: trimmedMessage,
          company,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.ok !== false) {
        setStatus(Status.Success);
        setStatusMessage(
          data.message || 'Thanks! Your message has been sent.'
        );
        setName('');
        setEmail('');
        setMessage('');
        setCompany('');
      } else {
        setStatus(Status.Error);
        setStatusMessage(
          data.error
            || 'We could not send your message. Please email info@taskpogo.com directly.'
        );
      }
    } catch (err) {
      setStatus(Status.Error);
      setStatusMessage(
        'We could not reach the contact service. Please email info@taskpogo.com directly.'
      );
    }
  }

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <h1 className="h3 mb-2">Support</h1>
      <p className="text-body-secondary">
        Answers to the questions we hear most often. If you don't see yours,
        send us a note at the bottom of the page.
      </p>

      <h2 className="h5 mt-4 mb-3">Frequently asked questions</h2>
      <Accordion>
        {faqs.map((faq, idx) => (
          <Accordion.Item eventKey={String(idx)} key={faq.id}>
            <Accordion.Header>{faq.q}</Accordion.Header>
            <Accordion.Body>{faq.a}</Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      <h2 className="h5 mt-5 mb-3">Questions or feedback?</h2>
      <p className="text-body-secondary">
        Send us a message and we'll get back to you. You can also email{' '}
        <a href="mailto:info@taskpogo.com">info@taskpogo.com</a> directly.
      </p>

      <Form onSubmit={handleSubmit} noValidate>
        <Form.Group className="mb-3" controlId="contact-name">
          <Form.Label>Name (optional)</Form.Label>
          <Form.Control
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            disabled={status === Status.Submitting}
          />
        </Form.Group>

        <Form.Group className="mb-3" controlId="contact-email">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            disabled={status === Status.Submitting}
            placeholder="you@example.com"
          />
          <Form.Text className="text-body-secondary">
            So we can reply to you.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3" controlId="contact-message">
          <Form.Label>Message</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            disabled={status === Status.Submitting}
          />
        </Form.Group>

        {/*
          Honeypot field. Hidden from real users (visually and from
          assistive tech) but present in the DOM so bots that fill every
          input give themselves away. Server-side, a non-empty `company`
          short-circuits to "success" without sending.
        */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: 1,
            height: 1,
            overflow: 'hidden',
          }}
        >
          <label htmlFor="contact-company">Company</label>
          <input
            id="contact-company"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </div>

        <Button type="submit" variant="primary" disabled={!canSubmit}>
          {status === Status.Submitting ? 'Sending…' : 'Send message'}
        </Button>

        {status === Status.Success && (
          <Alert variant="success" className="mt-3 mb-0">
            {statusMessage}
          </Alert>
        )}
        {status === Status.Error && (
          <Alert variant="danger" className="mt-3 mb-0">
            {statusMessage}
          </Alert>
        )}
      </Form>
    </div>
  );
};

export default Support;
